/**
 * Client for the engine worker.
 *
 * Requests can overlap, so each is tagged and a reply arriving after the user
 * has moved on is ignored. Cancellation is implemented by terminating the
 * worker: the WebAssembly call itself is synchronous, so this is the only way
 * to stop a long reduction immediately.
 */

import type { Budget, CaseSpec } from '@spec';
import type {
  AnalyzeResult,
  CheckReport,
  NormalizeResult,
  ProgramReport,
  Request,
  Response,
  TraceResult,
} from './protocol';

/** Thrown for requests that were abandoned because the user moved on. */
export class Cancelled extends Error {
  constructor() {
    super('the request was cancelled');
    this.name = 'Cancelled';
  }
}

/** How the hosts word a stack overflow: Firefox, V8 and Safari respectively. */
const STACK_OVERFLOW = /too much recursion|call stack|stack overflow/i;

/**
 * What to tell the player about a failed engine call.
 *
 * A reduction that overruns the host's stack surfaces as an exception with the
 * host's own wording, which reads like a verdict on the program and mentions
 * an implementation detail the player cannot act on. Replacing it says what
 * happened: the engine stopped, so nothing was decided. A limit the engine
 * reports itself is a different thing and arrives as a verdict, not here.
 */
export function describeFailure(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (STACK_OVERFLOW.test(message)) {
    return 'the engine ran out of stack while reducing this program, so there is no verdict for it';
  }
  return message;
}

/** `Omit` does not distribute over a union, so spell the distributive form out. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

type RequestBody = DistributiveOmit<Request, 'id'>;

interface Pending {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}

export class Engine {
  #worker: Worker | null = null;
  #pending = new Map<number, Pending>();
  #nextId = 1;
  /** Rejects the ready promise of the worker that is being replaced. */
  #readyReject: ((error: Error) => void) | null = null;
  /** Set once the worker has died. See {@link Engine.#startWorker}. */
  #ready: Promise<string> = this.#startWorker();
  /**
   * Set when the engine cannot start. WebAssembly cannot be interrupted, so a
   * runaway reduction is stopped by terminating the worker; if the module
   * never loads, every later call has to fail rather than wait forever for an
   * answer that is never coming.
   */
  #failure: Error | null = null;

  /** Resolves with the engine version once the module is loaded. */
  ready(): Promise<string> {
    return this.#ready;
  }

  check(
    definitions: Record<string, string>,
    cases: CaseSpec[],
    budget?: Budget,
  ): Promise<CheckReport> {
    return this.#call<CheckReport>({ kind: 'check', definitions, cases, budget });
  }

  normalize(
    definitions: Record<string, string>,
    term: string,
    options: { allowUnknownNames?: boolean; budget?: Budget } = {},
  ): Promise<NormalizeResult> {
    return this.#call<NormalizeResult>({
      kind: 'normalize',
      definitions,
      term,
      budget: options.budget,
      allowUnknownNames: options.allowUnknownNames,
    });
  }

  trace(
    definitions: Record<string, string>,
    term: string,
    steps: number,
    options: { allowUnknownNames?: boolean; budget?: Budget } = {},
  ): Promise<TraceResult> {
    return this.#call<TraceResult>({
      kind: 'trace',
      definitions,
      term,
      steps,
      budget: options.budget,
      allowUnknownNames: options.allowUnknownNames,
    });
  }

  analyze(sources: string[]): Promise<AnalyzeResult> {
    return this.#call<AnalyzeResult>({ kind: 'analyze', sources });
  }

  /** Parses each source as a program of `name = term` definitions. */
  parsePrograms(sources: string[]): Promise<{ programs: ProgramReport[] }> {
    return this.#call<{ programs: ProgramReport[] }>({ kind: 'parsePrograms', sources });
  }

  /** Abandons every in-flight request and starts a fresh worker. */
  cancel(): void {
    this.#failAll(new Cancelled());
    // A request still waiting for the outgoing worker to start cannot reach it
    // now, so it is settled the same way instead of hanging.
    this.#readyReject?.(new Cancelled());
    this.#worker?.terminate();
    this.#failure = null;
    this.#ready = this.#startWorker();
  }

  #call<T>(request: RequestBody): Promise<T> {
    const failure = this.#failure;
    if (failure !== null) {
      return Promise.reject(failure);
    }
    const id = this.#nextId;
    this.#nextId += 1;
    return this.#ready.then(
      () =>
        new Promise<T>((resolve, reject) => {
          this.#pending.set(id, { resolve: (value) => resolve(value as T), reject });
          this.#worker?.postMessage({ ...request, id });
        }),
    );
  }

  /** Rejects everything in flight. */
  #failAll(error: Error): void {
    const pending = this.#pending;
    this.#pending = new Map();
    for (const entry of pending.values()) {
      entry.reject(error);
    }
  }

  /** Records that the engine cannot start, and fails everything waiting on it. */
  #fail(message: string): void {
    const error = new Error(describeFailure(message));
    this.#failure = error;
    this.#failAll(error);
  }

  /**
   * Starts a worker and waits for it to report for duty.
   *
   * A worker that dies after starting is replaced, so the engine recovers by
   * itself instead of staying broken until a reload. One that never starts is
   * not replaced: replacing it would retry the same doomed load forever, so
   * the failure is latched and every later call fails fast with it.
   */
  #startWorker(): Promise<string> {
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.#worker = worker;
    const ready = new Promise<string>((resolve, reject) => {
      this.#readyReject = reject;
      let started = false;
      worker.addEventListener('message', (event: MessageEvent<Response>) => {
        const message = event.data;
        if (message.kind === 'ready') {
          started = true;
          resolve(message.version);
          return;
        }
        if (message.kind === 'unavailable') {
          fail(message.message);
          return;
        }
        const pending = this.#pending.get(message.id);
        if (!pending) {
          return;
        }
        this.#pending.delete(message.id);
        if (message.kind === 'result') {
          pending.resolve(message.payload);
        } else {
          pending.reject(new Error(describeFailure(message.message)));
        }
      });
      worker.addEventListener('error', (event) =>
        fail(event.message || 'the engine worker failed to start'),
      );
      worker.addEventListener('messageerror', () =>
        fail('the engine worker sent a message that could not be read'),
      );
      const fail = (message: string): void => {
        if (!started) {
          this.#fail(message);
          reject(new Error(describeFailure(message)));
          return;
        }
        this.#failAll(new Error(describeFailure(message)));
        this.#ready = this.#startWorker();
      };
    });
    // A ready promise nobody is waiting on must not surface as an unhandled
    // rejection: the failure reaches whoever asks next through `#failure`.
    ready.catch(() => {});
    return ready;
  }
}
